import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  XMarkIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from '../../utils/notifications';

const MenuManagement = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Add Item Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    price: '',
    category: ''
  });
  const [addingItem, setAddingItem] = useState(false);
  
  // Edit Price Modal State
  const [showEditPriceModal, setShowEditPriceModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [updatingPrice, setUpdatingPrice] = useState(false);

  // Delete Confirmation Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deletingItem, setDeletingItem] = useState(false);

  useEffect(() => {
    // Fetch all items on mount, category filter will be applied after fetch
    fetchMenuItems('', selectedCategory, 'all');
  }, [selectedCategory, availabilityFilter]);

  useEffect(() => {
    // Fetch categories and initial items on mount
    fetchMenuItemsAndCategories();
  }, []);

  const fetchMenuItemsAndCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/menu-items', { params: { per_page: 10000 } });
      const items = response.data.data.data || response.data.data || [];
      
      // Extract unique categories from items
      const uniqueCategories = [...new Set(items.map(item => item.category).filter(Boolean))];
      setCategories(uniqueCategories.sort());
      
      // Set initial items
      setMenuItems(Array.isArray(items) ? items : []);
      console.log('Fetched items:', items); // Debug log
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to fetch menu items');
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async (search = '', category = '', filter = 'all') => {
    try {
      setLoading(true);
      const params = { per_page: 10000 };
      
      // Use 'q' parameter for search (as per backend API)
      if (search && search.trim()) {
        params.q = search.trim();
      }
      
      // Use 'category' parameter for category filter
      if (category && category.trim()) {
        params.category = category.trim();
      }

      console.log('Fetching with params:', params); // Debug log
      const response = await api.get('/menu-items', { params });

      let items = response.data.data.data || response.data.data || [];
      items = Array.isArray(items) ? items : [];
      
      // Apply status filter on frontend since backend doesn't support it
      if (filter !== 'all') {
        items = items.filter(item => item.status === filter);
      }
      
      setMenuItems(items);
      console.log('Fetched and filtered items:', items); // Debug log
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to fetch menu items');
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  const updateItemStatus = async (menuItemId, newStatus) => {
    try {
      // Use standard PUT endpoint to update menu item
      await api.put(`/menu-items/${menuItemId}`, { 
        status: newStatus,
        is_available: newStatus === 'available'
      });
      
      // Update local state
      setMenuItems(menuItems.map(item => 
        item.menu_item_id === menuItemId 
          ? { ...item, status: newStatus, is_available: newStatus === 'available' }
          : item
      ));
      
      toast.success(`Menu item marked as ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update menu item status');
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    
    if (!newItem.name.trim() || !newItem.price || !newItem.category.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (parseFloat(newItem.price) <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }

    setAddingItem(true);
    try {
      const response = await api.post('/menu-items/quick', {
        name: newItem.name.trim(),
        price: parseFloat(newItem.price),
        category: newItem.category.trim()
      });

      // Add new item to the list
      setMenuItems([response.data.data, ...menuItems]);
      
      // Update categories if new category added
      if (!categories.includes(newItem.category.trim())) {
        setCategories([...categories, newItem.category.trim()].sort());
      }

      toast.success('Menu item added successfully');
      setShowAddModal(false);
      setNewItem({ name: '', price: '', category: '' });
    } catch (error) {
      console.error('Error adding menu item:', error);
      toast.error(error.response?.data?.message || 'Failed to add menu item');
    } finally {
      setAddingItem(false);
    }
  };

  const openEditPriceModal = (item) => {
    setEditingItem(item);
    setEditPrice(item.price.toString());
    setShowEditPriceModal(true);
  };

  const handleUpdatePrice = async (e) => {
    e.preventDefault();

    if (!editPrice || parseFloat(editPrice) <= 0) {
      toast.error('Please enter a valid price greater than 0');
      return;
    }

    setUpdatingPrice(true);
    try {
      const response = await api.patch(`/menu-items/${editingItem.menu_item_id}/price`, {
        price: parseFloat(editPrice)
      });

      // Update local state
      setMenuItems(menuItems.map(item => 
        item.menu_item_id === editingItem.menu_item_id 
          ? { ...item, price: parseFloat(editPrice) }
          : item
      ));

      toast.success('Price updated successfully');
      setShowEditPriceModal(false);
      setEditingItem(null);
      setEditPrice('');
    } catch (error) {
      console.error('Error updating price:', error);
      toast.error(error.response?.data?.message || 'Failed to update price');
    } finally {
      setUpdatingPrice(false);
    }
  };

  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;

    setDeletingItem(true);
    try {
      await api.delete(`/menu-items/${itemToDelete.menu_item_id}`);

      // Update local state
      setMenuItems(menuItems.filter(item => item.menu_item_id !== itemToDelete.menu_item_id));

      toast.success('Menu item deleted successfully');
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(error.response?.data?.message || 'Failed to delete item');
    } finally {
      setDeletingItem(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Fetch only when Search button is clicked
    fetchMenuItems(searchTerm, selectedCategory, availabilityFilter);
  };

  const getStatusBadge = (status) => {
    if (status === 'available') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-4 h-4 mr-1" />
          Available
        </span>
      );
    } else if (status === 'sold_out') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircleIcon className="w-4 h-4 mr-1" />
          Sold Out
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <XCircleIcon className="w-4 h-4 mr-1" />
        Unavailable
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Menu Management</h2>
            <p className="mt-1 text-xs sm:text-sm text-gray-600">
              Manage menu items and mark items as available or sold out
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors shadow-sm text-sm font-medium"
          >
            <PlusIcon className="h-5 w-5" />
            Add Menu Item
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow-sm rounded-lg p-3 sm:p-6">
        <form onSubmit={handleSearch} className="space-y-3 sm:space-y-4">
          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search menu items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm"
                />
              </div>
            </div>
            <button
              type="submit"
              className="px-3 sm:px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors text-sm whitespace-nowrap"
            >
              Search
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Filters</span>
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-4 border-t">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Status Filter
                </label>
                <select
                  value={availabilityFilter}
                  onChange={(e) => setAvailabilityFilter(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm"
                >
                  <option value="all">All Items</option>
                  <option value="available">Available Only</option>
                  <option value="sold_out">Sold Out Only</option>
                  <option value="unavailable">Unavailable Only</option>
                </select>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Total Items</div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">{menuItems.length}</div>
        </div>
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Available</div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold text-green-600">
            {menuItems.filter(item => item.status === 'available').length}
          </div>
        </div>
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Sold Out</div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold text-red-600">
            {menuItems.filter(item => item.status === 'sold_out').length}
          </div>
        </div>
        <div className="bg-white shadow-sm rounded-lg p-3 sm:p-5">
          <div className="text-[10px] sm:text-sm font-medium text-gray-500 truncate">Unavailable</div>
          <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold text-gray-600">
            {menuItems.filter(item => item.status === 'unavailable').length}
          </div>
        </div>
      </div>

      {/* Menu Items Table/Cards */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {/* Desktop/Tablet Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item Name
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="hidden lg:table-cell px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {menuItems.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 lg:px-6 py-12 text-center text-sm text-gray-500">
                    No menu items found
                  </td>
                </tr>
              ) : (
                menuItems.map((item) => (
                  <tr key={item.menu_item_id} className="hover:bg-gray-50">
                    <td className="px-4 lg:px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      <div className="text-xs text-gray-500 line-clamp-1">{item.description}</div>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <span className="text-sm text-gray-900">{item.category}</span>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">
                        ₱{parseFloat(item.price).toFixed(2)}
                      </span>
                    </td>
                    <td className="hidden lg:table-cell px-4 lg:px-6 py-4">
                      {getStatusBadge(item.status || (item.is_available ? 'available' : 'unavailable'))}
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditPriceModal(item)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded-md hover:bg-amber-100 transition-colors"
                          title="Edit Price"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                          Edit Price
                        </button>
                        <button
                          onClick={() => openDeleteModal(item)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                          title="Delete Item"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                          Delete
                        </button>
                        <select
                          value={item.status || 'available'}
                          onChange={(e) => updateItemStatus(item.menu_item_id, e.target.value)}
                          className="flex-1 min-w-[100px] px-2 py-1.5 rounded border border-gray-300 bg-white text-xs font-medium text-gray-700 hover:border-gray-400 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors"
                        >
                          <option value="available">✓ Available</option>
                          <option value="sold_out">✗ Sold Out</option>
                          <option value="unavailable">⊘ Unavailable</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-gray-200">
          {menuItems.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-gray-500">
              No menu items found
            </div>
          ) : (
            menuItems.map((item, index) => (
              <div key={item.menu_item_id} className="p-4 hover:bg-gray-50">
                <div className="space-y-3">
                  {/* Header with name and status */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                      {item.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.description}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {getStatusBadge(item.status || (item.is_available ? 'available' : 'unavailable'))}
                    </div>
                  </div>
                  
                  {/* Category and Price */}
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {item.category}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      ₱{parseFloat(item.price).toFixed(2)}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => openEditPriceModal(item)}
                        className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 rounded-md hover:bg-amber-100 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Edit Price
                      </button>
                      <button
                        onClick={() => openDeleteModal(item)}
                        className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Update Status:
                      </label>
                      <select
                        value={item.status || 'available'}
                        onChange={(e) => updateItemStatus(item.menu_item_id, e.target.value)}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:border-gray-400 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors shadow-sm"
                      >
                        <option value="available">✓ Available</option>
                        <option value="sold_out">✗ Sold Out</option>
                        <option value="unavailable">⊘ Unavailable</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => !addingItem && setShowAddModal(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Add New Menu Item</h3>
                <button
                  onClick={() => !addingItem && setShowAddModal(false)}
                  disabled={addingItem}
                  className="text-gray-400 hover:text-gray-500 transition-colors disabled:opacity-50"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleAddItem} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    placeholder="e.g., Fried Chicken"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    disabled={addingItem}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (₱) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newItem.price}
                    onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    disabled={addingItem}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    list="categories-list"
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    placeholder="e.g., Main Course, Beverages"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    disabled={addingItem}
                    required
                  />
                  <datalist id="categories-list">
                    {categories.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                  <p className="mt-1 text-xs text-gray-500">
                    Select existing or type a new category
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    disabled={addingItem}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addingItem}
                    className="flex-1 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {addingItem ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Adding...
                      </>
                    ) : (
                      <>
                        <PlusIcon className="h-4 w-4" />
                        Add Item
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Price Modal */}
      {showEditPriceModal && editingItem && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => !updatingPrice && setShowEditPriceModal(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Price</h3>
                <button
                  onClick={() => !updatingPrice && setShowEditPriceModal(false)}
                  disabled={updatingPrice}
                  className="text-gray-400 hover:text-gray-500 transition-colors disabled:opacity-50"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Item Name</p>
                <p className="text-base font-semibold text-gray-900">{editingItem.name}</p>
                <p className="text-xs text-gray-500 mt-1">Category: {editingItem.category}</p>
              </div>

              <form onSubmit={handleUpdatePrice} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Price
                  </label>
                  <div className="text-xl font-bold text-gray-900 mb-3">
                    ₱{parseFloat(editingItem.price).toFixed(2)}
                  </div>
                  
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Price (₱) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    disabled={updatingPrice}
                    required
                    autoFocus
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditPriceModal(false)}
                    disabled={updatingPrice}
                    className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updatingPrice}
                    className="flex-1 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {updatingPrice ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <PencilIcon className="h-4 w-4" />
                        Update Price
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => !deletingItem && setShowDeleteModal(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Delete Menu Item</h3>
                <button
                  onClick={() => !deletingItem && setShowDeleteModal(false)}
                  disabled={deletingItem}
                  className="text-gray-400 hover:text-gray-500 transition-colors disabled:opacity-50"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">You are about to delete:</p>
                <p className="text-base font-semibold text-gray-900">{itemToDelete.name}</p>
                <p className="text-xs text-gray-500 mt-1">Category: {itemToDelete.category}</p>
                <p className="text-xs text-gray-500">Price: ₱{parseFloat(itemToDelete.price).toFixed(2)}</p>
              </div>

              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 font-medium">⚠️ Warning</p>
                <p className="text-xs text-yellow-700 mt-1">
                  This action cannot be undone. The menu item will be permanently deleted from the database.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deletingItem}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteItem}
                  disabled={deletingItem}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deletingItem ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      Delete Item
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuManagement;
